import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog } from '@mend/mui';
import { Button, Stack, TextField, Typography } from '@mui/material';
import { usePostHog } from '@posthog/react';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { me } from '#lib/react-query/queries.ts';

/**
 * Survey ID from the PostHog dashboard (Surveys → "Readiness feedback", display mode: API).
 * Responses are attributed to this survey via the $survey_id property on the captured events.
 */
const READINESS_FEEDBACK_SURVEY_ID = '019e8e47-7e8a-0000-4878-13c576dd1918';

const MAX_FEEDBACK_LENGTH = 1000;

const schema = z.object({
  feedback: z
    .string()
    .trim()
    .min(1, 'Please enter some feedback')
    .max(MAX_FEEDBACK_LENGTH, 'Feedback is too long'),
});

interface FeedbackModalProps {
  onClose: () => void;
}

export function FeedbackModal({ onClose }: FeedbackModalProps) {
  const titleId = React.useId();
  const feedbackFieldId = React.useId();
  const posthog = usePostHog();
  const { data: { user, org } = {} } = useQuery(me);
  const { enqueueSnackbar } = useSnackbar();
  const hasSubmittedRef = React.useRef(false);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { feedback: '' },
  });

  // PostHog's Surveys product computes response rate from "survey shown" vs "survey sent", so record
  // the impression when the modal opens.
  React.useEffect(() => {
    posthog.capture('survey shown', { $survey_id: READINESS_FEEDBACK_SURVEY_ID });
  }, [posthog]);

  const onSubmit = form.handleSubmit((data) => {
    // Since Feb 2025 PostHog reads responses from `$survey_response_<question_id>`, not the legacy
    // `$survey_response`. Resolve the question id from the live survey definition so it isn't
    // hard-coded (and survives question edits). Note: this requires the survey to be launched —
    // PostHog only returns running surveys to the client.
    posthog.getSurveys((surveys) => {
      const survey = surveys.find((s) => s.id === READINESS_FEEDBACK_SURVEY_ID);
      const questionId = survey?.questions[0]?.id;

      posthog.capture('survey sent', {
        $survey_id: READINESS_FEEDBACK_SURVEY_ID,
        ...(questionId ? { [`$survey_response_${questionId}`]: data.feedback } : {}),
        $survey_completed: true,
        // Attach the responder's info as event properties (no identify) so responses are
        // contactable. PII rides only on this explicit submit — the "survey shown"/"dismissed"
        // impression events stay anonymous.
        email: user?.email,
        role: user?.role,
        org_id: org?.id,
        org_name: org?.name,
      });
    });
    hasSubmittedRef.current = true;
    enqueueSnackbar('Thanks for your feedback!', { variant: 'success' });
    onClose();
  });

  function handleClose() {
    if (!hasSubmittedRef.current) {
      posthog.capture('survey dismissed', { $survey_id: READINESS_FEEDBACK_SURVEY_ID });
    }
    onClose();
  }

  return (
    <Dialog.Root open onClose={handleClose} maxWidth="xs" fullWidth aria-labelledby={titleId}>
      {/* oxlint-disable-next-line typescript/no-misused-promises */}
      <form onSubmit={onSubmit}>
        <Dialog.Header>
          <Dialog.Title id={titleId}>Share your thoughts</Dialog.Title>
          <Dialog.Description>Help us shape the future of Mia</Dialog.Description>
        </Dialog.Header>

        <Dialog.Content>
          <Stack direction="column" spacing={1}>
            <Typography
              component="label"
              htmlFor={feedbackFieldId}
              variant="subtitle2"
              fontWeight={700}
            >
              What&apos;s working well? What could be better?
            </Typography>
            <TextField
              {...form.register('feedback')}
              id={feedbackFieldId}
              autoFocus
              multiline
              minRows={5}
              placeholder="Tell us anything — features you'd love, things that feel off, or ideas you want to see…"
              error={!!form.formState.errors.feedback}
              helperText={form.formState.errors.feedback?.message}
              fullWidth
            />
          </Stack>
        </Dialog.Content>

        <Dialog.Footer>
          <Button type="submit" variant="contained" color="quaternary" size="large" fullWidth>
            Send Feedback
          </Button>
        </Dialog.Footer>
      </form>
    </Dialog.Root>
  );
}
